import * as lodash from 'lodash';

export function getResumeContentPrompt(content: string) {
  let prompt = `
Please convert the following resume (in HTML format or a pure text) to markdown format and provide additional insights based on the person's background. Output the markdown content without using code block marks and include the following sections:

1. Portrait Image Url
2. First Name
3. Middle Name
4. Last Name
5. Gender (inferred: male or female)
6. Personality (guessed: professional, casual, rude, friendly, curious, disinterested, or brief)
7. Pain Points (3-5 specific points the customer may experience)
8. Personal Information
9. Work Experience
10. Most recent company
11. Most recent position
12. Project Experience
13. Education
14. Skills
15. Certifications
16. Awards
17. Publications
18. Languages
19. Interests
20. References
21. Other relevant details

If any section is not present in the resume, output an empty string for that section.

<RESUME_HTML_FORMAT>
${content}
</RESUME_HTML_FORMAT>

Please structure your response as a JSON object matching the specified parameters in the function definition.
`;

  return prompt;
}

export function getResumeContentFunctions() {
  return [
    {
      name: 'convert_resume_to_markdown_format',
      description:
        'Output the text in markdown format for this resume. Also make some guess based on the background of the person mentioned in the resume.',
      parameters: {
        type: 'object',
        properties: {
          painPoints: {
            type: 'array',
            description: '3-5 specific pain points the customer may experience.',
            items: { type: 'string' },
            minItems: 3,
            maxItems: 5,
          },
          gender: {
            type: 'string',
            desciption: 'Pick a gender from male or female based on the customer profile.',
            enum: ['male', 'female'],
          },
          portraitImageUrl: {
            type: 'string',
            description: 'The portrait image url of the resume if any.',
          },
          firstName: {
            type: 'string',
            description: 'If available, the first name of the person mentioned in the resume.',
          },
          middleName: {
            type: 'string',
            description: 'If available, the middle name of the person mentioned in the resume.',
          },
          lastName: {
            type: 'string',
            description: 'If available, the last name of the person mentioned in the resume.',
          },
          personality: {
            type: 'string',
            desciption:
              'Based on the resume, provide your best guess of the person’s personality. The personality should be one of the following: professional, casual, rude, friendly, curious, disinterested, brief.',
            enum: ['professional', 'casual', 'rude', 'friendly', 'curious', 'disinterested', 'brief'],
          },
          personalInformation: { type: 'object', description: "Customer's personal information" },
          workExperience: { type: 'string', description: "Customer's work experience. Extract specific details, do not miss any information, do not change words, do not summarize, do not remove numbers." },
          mostRecentCompany: { type: 'string', description: "Customer's most recent company" },
          mostRecentPosition: { type: 'string', description: "Customer's most recent position" },
          projectExperience: { type: 'string', description: "Customer's project experience" },
          education: { type: 'string', description: "Customer's education" },
          skills: { type: 'array', description: "Customer's skills", items: { type: 'string' } },
          certifications: { type: 'array', description: "Customer's certifications", items: { type: 'string' } },
          awards: { type: 'array', description: "Customer's awards", items: { type: 'string' } },
          publications: { type: 'array', description: "Customer's publications", items: { type: 'string' } },
          languages: { type: 'array', description: 'Languages spoken by the customer', items: { type: 'string' } },
          interests: { type: 'array', description: "Customer's interests", items: { type: 'string' } },
          references: { type: 'array', description: "Customer's references", items: { type: 'string' } },
          otherDetails: { type: 'string', description: 'Other relevant details about the customer' },
        },
      },
    },
  ];
}

export function convertResumeJsonToMarkdown(jsonData: Record<string, string>, includeName: boolean): string {
  const resumeJsonContent = lodash.cloneDeep(jsonData) as any;

  delete resumeJsonContent.personalityIdList;
  delete resumeJsonContent.voice;

  if (!includeName) {
    delete resumeJsonContent.lastName;
    delete resumeJsonContent.firstName;
  }

  let markdownTxt = '';
  for (const [key, value] of Object.entries(resumeJsonContent)) {
    markdownTxt += `## ${key}\n\n`;
    if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
      markdownTxt += value.map((item) => `- ${item}`).join('\n') + '\n\n';
    } else {
      markdownTxt += `${value}\n\n`;
    }
  }
  return markdownTxt;
}
